import React from 'react';
import { Phone, Mail, MapPin, Factory } from 'lucide-react';
import { useContent } from '../lib/ContentContext';
import { PageHero } from '../components/site/PageHero';
import { ContactForm } from '../components/site/ContactForm';

const ICONS = { Phone, Mail, MapPin, Factory };

export default function Contact() {
  const { content } = useContent();
  const page = content?.contact_page;
  if (!page) return null;

  return (
    <div>
      <PageHero title={page.hero_title} subtitle={page.hero_subtitle} image={page.hero_image} />

      <section className="py-14 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 lg:gap-14">
            {/* Form */}
            <div className="lg:col-span-3">
              <h2 className="text-[#2E8B57] font-extrabold text-2xl sm:text-3xl tracking-tight">{page.form_title}</h2>
              <div className="mt-8">
                <ContactForm variant="full" industryOptions={page.industry_options || []} />
              </div>
            </div>

            {/* Info cards */}
            <div className="lg:col-span-2 space-y-5">
              {(page.info_cards || []).map((card, i) => {
                const Icon = ICONS[card.icon] || Phone;
                return (
                  <div key={i} data-testid="contact-info-card" className="bg-[#F6F7F9] rounded-2xl p-6 flex gap-5">
                    <div className="h-12 w-12 rounded-xl bg-[#26247B] flex items-center justify-center shrink-0">
                      <Icon className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-[#26247B] font-extrabold">{card.title}</h3>
                      <div className="mt-1.5 space-y-1">
                        {(card.lines || []).map((line, j) => (
                          <p key={j} className="text-black/80 text-sm leading-6">{line}</p>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Location maps */}
      {(page.maps || []).length > 0 && (
        <section className="pb-14 sm:pb-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {(page.maps || []).map((m, i) => (
                <div key={i} data-testid="contact-map-card">
                  <h3 className="text-[#26247B] font-extrabold text-lg">{m.title}</h3>
                  <p className="mt-1.5 text-black/80 text-sm leading-6">{m.address}</p>
                  <div className="mt-4 rounded-2xl overflow-hidden border border-[#E6E8EE] shadow-[0_8px_20px_rgba(17,24,39,0.08)] bg-[#F6F7F9]">
                    <iframe
                      title={m.title}
                      src={`https://maps.google.com/maps?q=${encodeURIComponent(m.address)}&z=15&output=embed`}
                      className="w-full h-[340px] border-0"
                      loading="lazy"
                      allowFullScreen
                      referrerPolicy="no-referrer-when-downgrade"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
